import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, take, tap } from 'rxjs';
import { AppProfileService } from '../../services/profile.service';
import { CustomerService } from '../../services/customer.service';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { CustomerContactUpdate, EMAIL_ADDRESS_MAX_LENGTH } from '../../common/CustomerContact';
import { FormRowComponent } from '../form-row/form-row.component';

/**
 * Self-service editing of the signed-in customer's CONTACT block — e-mail, phone, fax,
 * website, description — over the BFF (backend #670, `PUT api/customer/{id}/contact`).
 * The e-mail is the customer's business contact address: buyers see it on the seller
 * page, it is printed in the invoice issuer block, and seller order notices go there. It is
 * distinct from any user's login. The parent shows this component to customer admins only
 * (the endpoint enforces the same); everything else on the customer stays Carrier-only.
 */
@Component({
    imports: [ReactiveFormsModule, CommonModule, AsyncClickDirective, FormRowComponent],
    selector: 'app-customer-contact-edit',
    templateUrl: './customer-contact-edit.component.html',
    styleUrls: ['./customer-contact-edit.component.css']
})
export class CustomerContactEditComponent implements OnInit {
  private profileService = inject(AppProfileService);
  private customerService = inject(CustomerService);
  private callApiService = inject(CallApiService);

  private customerId: string | null = null;

  public contactForm = new FormGroup({
    email: new FormControl('', [Validators.maxLength(EMAIL_ADDRESS_MAX_LENGTH), Validators.email]),
    phone: new FormControl('', [Validators.maxLength(100)]),
    fax: new FormControl('', [Validators.maxLength(100)]),
    internet: new FormControl('', [Validators.maxLength(500)]),
    description: new FormControl('', [Validators.maxLength(5000)]),
  });

  ngOnInit(): void {
    this.profileService.currentProfile$.pipe(take(1)).subscribe((profile) => {
      this.customerId = profile?.customer?.id ?? null;
      this.contactForm.patchValue({
        email: profile?.customer?.email ?? '',
        phone: profile?.customer?.phone ?? '',
        fax: profile?.customer?.fax ?? '',
        internet: profile?.customer?.internet ?? '',
        description: profile?.customer?.description ?? '',
      });
      this.contactForm.markAsPristine();
    });
  }

  // Needs the form value → returns a factory: [asyncClick]="save(contactForm.value)".
  save = (formValue: any) => (): Observable<unknown> => {
    const request: CustomerContactUpdate = {
      email: blankToNull(formValue.email),
      phone: blankToNull(formValue.phone),
      fax: blankToNull(formValue.fax),
      internet: blankToNull(formValue.internet),
      description: blankToNull(formValue.description),
    };

    return this.callApiService
      .callWithToasts$(
        this.customerService.updateContact(this.customerId, request),
        $localize`Ihre Kontaktdaten wurden gespeichert`,
        $localize`Ihre Kontaktdaten konnten nicht gespeichert werden`,
      )
      // "Meine Daten" and the seller page bind the BFF profile — refresh so the new
      // values show immediately.
      .pipe(
        tap(() => {
          this.contactForm.markAsPristine();
          this.profileService.refreshCurrentProfile();
        })
      );
  };
}

function blankToNull(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text.length === 0 ? null : text;
}
